/**
 * Éditeur des points d'équipement internes (facilities) — pas des POI publics.
 */

import { useCallback, useEffect, useState } from 'react'
import { MapPicker, type MapPickerPoint } from './MapPicker'
import { existingPointsOf } from './mapPickerPoints'
import { EntityPhotosStrip } from './EntityPhotosStrip'
import { useOrgSiteMapView } from '../hooks/useOrgSiteMapView'
import {
  createOrganizationFacility,
  deleteOrganizationFacility,
  deleteOrganizationFacilityPhoto,
  fetchOrganizationFacilities,
  uploadOrganizationFacilityPhoto,
  type OrganizationFacility,
  type OrganizationFacilityTypeOption,
  HttpError,
} from '../services/organizationApi'
import './OrganizationFacilitiesEditor.css'
import { useTranslation } from 'react-i18next'

interface Props {
  organizationId: number
  defaultCenter?: [number, number]
}

export function OrganizationFacilitiesEditor({ organizationId, defaultCenter }: Props) {
  const { t: tr } = useTranslation('organizations')
  const { siteView, mapKey } = useOrgSiteMapView(organizationId, defaultCenter)
  const [items, setItems] = useState<OrganizationFacility[]>([])
  const [types, setTypes] = useState<OrganizationFacilityTypeOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [facilityType, setFacilityType] = useState('showers')
  const [name, setName] = useState('')
  const [infoText, setInfoText] = useState('')
  const [summerPrice, setSummerPrice] = useState('')
  const [point, setPoint] = useState<MapPickerPoint | null>(null)
  const [saving, setSaving] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchOrganizationFacilities(organizationId)
      setItems(data.results)
      setTypes(data.facility_types)
      if (data.facility_types.length && !data.facility_types.some((t) => t.value === facilityType)) {
        setFacilityType(data.facility_types[0].value)
      }
    } catch (err) {
      setError(err instanceof HttpError ? err.message : tr('OrganizationFacilitiesEditor.chargement_impossible'))
    } finally {
      setLoading(false)
    }
  }, [organizationId, facilityType])

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload on org change only
  }, [organizationId])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!point) {
      setError(tr('OrganizationFacilitiesEditor.cliquez_carte_positionner_equipement'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      await createOrganizationFacility(organizationId, {
        facility_type: facilityType,
        name: name.trim() || undefined,
        info_text: infoText.trim() || undefined,
        summer_price: summerPrice.trim() || undefined,
        location: point,
      })
      setName('')
      setInfoText('')
      setSummerPrice('')
      setPoint(null)
      await reload()
    } catch (err) {
      setError(err instanceof HttpError ? err.message : tr('OrganizationFacilitiesEditor.enregistrement_impossible'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm(tr('OrganizationFacilitiesEditor.supprimer_cet_equipement'))) return
    try {
      await deleteOrganizationFacility(organizationId, id)
      await reload()
    } catch (err) {
      setError(err instanceof HttpError ? err.message : tr('OrganizationFacilitiesEditor.suppression_impossible'))
    }
  }

  return (
    <section className="org-facilities">
      <header className="org-facilities__header">
        <div>
          <h2 className="org-facilities__title">{tr('OrganizationFacilitiesEditor.equipements_site')}</h2>
          <p className="org-facilities__hint">
            {tr('OrganizationFacilitiesEditor.points_parc_meme_types_poi')}
            {siteView.ready
              ? tr('OrganizationFacilitiesEditor.cliquez_carte_placer_point')
              : tr('OrganizationFacilitiesEditor.position_site_inconnue_zoomez_manuelleme')}
          </p>
        </div>
      </header>

      {error && (
        <p className="org-facilities__error" role="alert">
          {error}
        </p>
      )}

      <div className="org-facilities__list-block">
        {loading ? (
          <p className="org-facilities__muted">{tr('OrganizationFacilitiesEditor.chargement')}</p>
        ) : items.length === 0 ? (
          <p className="org-facilities__muted org-facilities__empty">
            {tr('OrganizationFacilitiesEditor.aucun_equipement_positionne_instant')}
          </p>
        ) : (
          <ul className="org-facilities__list org-facilities__list--tall">
            {items.map((f) => (
              <li key={f.id} className="org-facilities__item org-facilities__item--stack">
                <div className="org-facilities__item-head">
                  <div>
                    <strong>{f.type_label || f.facility_type_label}</strong>
                    {f.name ? <span> — {f.name}</span> : null}
                    {f.summer_price != null && f.summer_price !== '' ? (
                      <span className="org-facilities__coords"> · {f.summer_price}&nbsp;$</span>
                    ) : null}
                    {f.location?.lat != null && f.location?.lng != null && (
                      <span className="org-facilities__coords">
                        {' '}
                        ({f.location.lat.toFixed(5)}, {f.location.lng.toFixed(5)})
                      </span>
                    )}
                    {f.info_text ? (
                      <p className="org-facilities__muted" style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>
                        {f.info_text}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="org-facilities__delete"
                    onClick={() => void handleDelete(f.id)}
                  >
                    {tr('OrganizationFacilitiesEditor.supprimer')}
                  </button>
                </div>
                <EntityPhotosStrip
                  photos={f.photos || []}
                  onUpload={async (file) => {
                    await uploadOrganizationFacilityPhoto(organizationId, f.id, { image: file })
                    await reload()
                  }}
                  onDelete={async (photoId) => {
                    await deleteOrganizationFacilityPhoto(organizationId, f.id, photoId)
                    await reload()
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <form className="org-facilities__toolbar" onSubmit={(e) => void handleAdd(e)}>
        <label className="org-facilities__field">
          <span>{tr('OrganizationFacilitiesEditor.type')}</span>
          <select value={facilityType} onChange={(e) => setFacilityType(e.target.value)}>
            {types.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="org-facilities__field org-facilities__field--grow">
          <span>{tr('OrganizationFacilitiesEditor.nom_optionnel')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={tr('OrganizationFacilitiesEditor.ex_douches_secteur')}
          />
        </label>
        <label className="org-facilities__field">
          <span>{tr('OrganizationFacilitiesEditor.prix_ete')}</span>
          <input
            value={summerPrice}
            onChange={(e) => setSummerPrice(e.target.value)}
            placeholder={tr('OrganizationFacilitiesEditor.ex_5')}
            inputMode="decimal"
          />
        </label>
        <label className="org-facilities__field org-facilities__field--grow">
          <span>{tr('OrganizationFacilitiesEditor.infos_specifiques')}</span>
          <input
            value={infoText}
            onChange={(e) => setInfoText(e.target.value)}
            placeholder={tr('OrganizationFacilitiesEditor.horaires_regles_liste_libre')}
          />
        </label>
        <div className="org-facilities__point-status" aria-live="polite">
          {point ? (
            <span>
              {tr('OrganizationFacilitiesEditor.point', { lat: point.lat.toFixed(5), lng: point.lng.toFixed(5) })}
            </span>
          ) : (
            <span className="org-facilities__muted">{tr('OrganizationFacilitiesEditor.aucun_point_selectionne')}</span>
          )}
        </div>
        <button type="submit" disabled={saving || !point} className="org-facilities__submit">
          {saving ? tr('OrganizationFacilitiesEditor.enregistrement') : tr('OrganizationFacilitiesEditor.ajouter_equipement')}
        </button>
      </form>

      <div className="org-facilities__map">
        <MapPicker
          key={`fac-${mapKey}`}
          value={point}
          onChange={setPoint}
          defaultCenter={siteView.center}
          defaultZoom={siteView.zoom}
          fitBounds={siteView.fitBounds}
          height="min(420px, 50vh)"
          showCoordinateFields={false}
          existingPoints={existingPointsOf(items, (f) => f.name || f.type_label || f.facility_type_label)}
        />
      </div>
    </section>
  )
}
